// src/hooks/useCentralEventStream.ts
// React hook for Central Foundation SSE event stream.
//
// ARCHITECTURE: Connects to Central SSE after boot is ready.
// Events trigger refetch of authoritative APIs — payloads are NOT source of truth.
//
// INVARIANTS:
// - Event payloads used only for workspace filtering or diagnostics
// - Business logic always refetches authoritative APIs
// - No boot loop on reconnect (uses updateBootContext, not setBootLoading)
// - No duplicate concurrent refetch calls (guarded by refs)

import { useEffect, useRef, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/store';
import {
  CentralEventStreamClient,
  type CentralBusinessEventType,
  type CentralEventPayload,
} from '@levelup/central-events';
import {
  selectBootStatus,
  selectBootCurrentWorkspace,
} from '../store/slices/bootContextSlice';
import { selectCentralAccessToken } from '../store/slices/centralAuthSlice';
import { CENTRAL_URL } from '../config/centralAuth';
import { setSseConnected } from '../store/slices/featureFlagsSlice';
import { refetchSwitchContext } from '../api/centralSuiteApi';
import { evaluateFeatureFlags } from '../api/centralFeaturesApi';
import { baseApi } from '../api/baseApi';

// ============================================================
// Event Categories
// ============================================================

// Events that trigger switch-context refetch
const CONTEXT_REFRESH_EVENTS: CentralBusinessEventType[] = [
  'workspace.member.added.v1',
  'workspace.member.removed.v1',
  'workspace.member.updated.v1',
  'entitlements.changed.v1',
  'subscription.status.changed.v1',
];

// Events that invalidate the Settings → Members lists.
//
// These are the canonical Central membership events (already consumed above for
// switch-context). Switch-context describes the CURRENT USER's own standing, so
// it does not refresh the roster — a change another admin makes to someone
// else's workspace role or per-app grants would otherwise leave this tab showing
// stale rows until a manual reload. The local save path invalidates these same
// tags through the mutation, so this only covers out-of-session changes.
const MEMBERSHIP_REFRESH_EVENTS: CentralBusinessEventType[] = [
  'workspace.member.added.v1',
  'workspace.member.removed.v1',
  'workspace.member.updated.v1',
];

// Events that trigger credits cache invalidation
const CREDITS_REFRESH_EVENTS: CentralBusinessEventType[] = ['credits.balance.changed.v1'];

// Events that trigger feature flag re-evaluation
const FLAGS_REFRESH_EVENTS: CentralBusinessEventType[] = ['feature.flags.changed.v1'];

// ============================================================
// Hook
// ============================================================

/**
 * Hook to connect to Central SSE event stream.
 *
 * Must be mounted inside BootGate children (after boot is ready).
 * Manages SSE lifecycle: connect, disconnect, reconnect with backoff.
 *
 * INVARIANTS:
 * - Connects only when bootStatus === 'ready' and workspaceId exists
 * - Disconnects on workspace switch or unmount
 * - Reconnect triggers safe refetch to catch missed events
 * - No duplicate concurrent refetch calls
 */
export function useCentralEventStream(): void {
  const dispatch = useAppDispatch();
  const bootStatus = useAppSelector(selectBootStatus);
  const currentWorkspace = useAppSelector(selectBootCurrentWorkspace);
  const accessToken = useAppSelector(selectCentralAccessToken);

  const clientRef = useRef<CentralEventStreamClient | null>(null);
  const isRefetchingRef = useRef(false);
  const isEvaluatingFlagsRef = useRef(false);

  // Verified field name: currentWorkspace.id (bootContextSlice.ts:36)
  const workspaceId = currentWorkspace?.id;

  /**
   * Safe refetch for switch-context.
   * Guarded against duplicate concurrent calls.
   * Uses updateBootContext (not setBootLoading) to avoid boot loops.
   */
  const safeRefetch = useCallback(async () => {
    if (isRefetchingRef.current || !accessToken || !workspaceId) return;

    isRefetchingRef.current = true;
    try {
      await refetchSwitchContext(accessToken, dispatch);

      // Also refresh flags after context refresh
      if (!isEvaluatingFlagsRef.current) {
        isEvaluatingFlagsRef.current = true;
        try {
          await evaluateFeatureFlags(accessToken, workspaceId, dispatch);
        } finally {
          isEvaluatingFlagsRef.current = false;
        }
      }
    } finally {
      isRefetchingRef.current = false;
    }
  }, [accessToken, workspaceId, dispatch]);

  /**
   * Safe refresh for feature flags only.
   * Guarded against duplicate concurrent calls.
   */
  const safeRefreshFlags = useCallback(async () => {
    if (isEvaluatingFlagsRef.current || !accessToken || !workspaceId) return;

    isEvaluatingFlagsRef.current = true;
    try {
      await evaluateFeatureFlags(accessToken, workspaceId, dispatch);
    } finally {
      isEvaluatingFlagsRef.current = false;
    }
  }, [accessToken, workspaceId, dispatch]);

  /**
   * Handle business events from SSE stream.
   * Maps event types to appropriate refresh actions.
   *
   * INVARIANT: Payloads used only for workspace filtering — not as source of truth.
   */
  const handleEvent = useCallback(
    (eventType: CentralBusinessEventType, payload: CentralEventPayload) => {
      // Workspace filtering: ignore events for different workspaces.
      // This is the ONLY use of payload content — not as source of truth.
      if (payload.workspaceId && payload.workspaceId !== workspaceId) {
        return;
      }

      if (CONTEXT_REFRESH_EVENTS.includes(eventType)) {
        safeRefetch();
      }

      if (MEMBERSHIP_REFRESH_EVENTS.includes(eventType)) {
        // Tag ids verified: platformMembersApi.ts — 'MEMBERS' / 'INVITES'.
        // Refetch only; the event payload is never read as membership truth.
        dispatch(
          baseApi.util.invalidateTags([
            { type: 'Team', id: 'MEMBERS' },
            { type: 'Team', id: 'INVITES' },
          ]),
        );
      }

      if (CREDITS_REFRESH_EVENTS.includes(eventType)) {
        // Tag name verified: 'Credits' (baseApi.ts:327, workspacesApi.ts:52, billingApi.ts:33)
        dispatch(baseApi.util.invalidateTags([{ type: 'Credits' }]));
      }

      if (FLAGS_REFRESH_EVENTS.includes(eventType)) {
        safeRefreshFlags();
      }
    },
    [workspaceId, dispatch, safeRefetch, safeRefreshFlags]
  );

  /**
   * Handle 'connected' event from Central.
   */
  const handleConnected = useCallback(() => {
    // Connection confirmed by Central — no action needed
  }, []);

  /**
   * Handle connection status changes.
   */
  const handleConnectionChange = useCallback(
    (connected: boolean) => {
      dispatch(setSseConnected(connected));
    },
    [dispatch]
  );

  /**
   * Handle reconnect — refetch to catch missed events.
   * Uses safeRefetch which doesn't trigger boot loops.
   */
  const handleReconnect = useCallback(() => {
    safeRefetch();
  }, [safeRefetch]);

  /**
   * SSE lifecycle effect.
   * Connects when boot is ready, disconnects on workspace change or unmount.
   */
  useEffect(() => {
    // Only connect when boot is ready AND we have a workspace AND access token
    if (bootStatus !== 'ready' || !workspaceId || !accessToken) {
      // Clean up if requirements lost
      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
      }
      return;
    }

    // If client exists, disconnect first (handles workspace change or token refresh)
    if (clientRef.current) {
      clientRef.current.disconnect();
      clientRef.current = null;
    }

    // Create new client with current workspace and access token
    const client = new CentralEventStreamClient({
      centralUrl: CENTRAL_URL,
      workspaceId,
      accessToken,
      onEvent: handleEvent,
      onConnected: handleConnected,
      onConnectionChange: handleConnectionChange,
      onReconnect: handleReconnect,
    });

    clientRef.current = client;
    client.connect();

    // Cleanup on unmount or dependency change
    return () => {
      client.disconnect();
      clientRef.current = null;
    };
  }, [bootStatus, workspaceId, accessToken, handleEvent, handleConnected, handleConnectionChange, handleReconnect]);
}
